import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";

import SelectBox from "~/components/form/select-box";
import { SubmitButton } from "~/components/form/submit-button";
import TextBox from "~/components/form/text-box";
import {
  addTemplate,
  editTemplate,
  getDocumentTypes,
} from "~/services/supervisor.services";

import CustomFileInput from "./custom-file-input";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { templateEditHeaderRoute } from "~/pages/all-routes";
import { AddTemplateProps } from "./add-template";
import { toast } from "react-toastify";

const AddTemplateSchema = z.object({
  modelno: z.string().optional(),
  doctype_id: z.string().min(1, { message: "This is a mandatory field" }),
  description: z
    .string()
    .min(1, { message: "This is a mandatory field" })
    .regex(/^[^<>()|'\"--#\-\[\]]*$/, {
      message: "Description cannot contain these special characters.",
    }),
  expression: z
    .string()
    .optional()
    .refine(
      (value) => {
        if (value) {
          return /^[^<>()|'\"--#\-\[\]]*$/.test(value); // Regex check
        }
        return true; // If the value is undefined or empty, pass the validation
      },
      {
        message: "Expression cannot contain these special characters.",
      }
    ),
  file_content: z.string().min(1, { message: "This is a mandatory field" }),
  file_extension: z.string().min(1, { message: "This is a mandatory field" }),
  priority: z.string().optional(),
  match_tag_1: z.string().optional(),
  match_tag_2: z.string().optional(),
  match_tag_3: z.string().optional(),
  match_tag_4: z.string().optional(),
  match_tag_5: z.string().optional(),
});

const EditTemplateSchema = z.object({
  modelno: z.string().optional(),
  doctype_id: z.string().optional(),
  description: z
    .string()
    .min(1, { message: "This is a mandatory field" })
    .regex(/^[^<>()|'\"--#\-\[\]]*$/, {
      message: "Description cannot contain these special characters.",
    }),
  expression: z
    .string()
    .optional()
    .refine(
      (value) => {
        if (value) {
          return /^[^<>()|'\"--#\-\[\]]*$/.test(value); // Regex check
        }
        return true; // If the value is undefined or empty, pass the validation
      },
      {
        message: "Expression cannot contain these special characters.",
      }
    ),
  file_content: z.string().optional(),
  file_extension: z.string().optional(),
  priority: z.string().optional(),
  match_tag_1: z.string().optional(),
  match_tag_2: z.string().optional(),
  match_tag_3: z.string().optional(),
  match_tag_4: z.string().optional(),
  match_tag_5: z.string().optional(),
});

type AddTemplateType = z.infer<typeof AddTemplateSchema>;
type EditTemplateType = z.infer<typeof EditTemplateSchema>;

type AddTemplateFormProps = Pick<AddTemplateProps, "mode" | "data">;
interface OptionType {
  value: string;
  key: string | number;
}

const AddTemplateForm = ({ mode = "add", data }: AddTemplateFormProps) => {
  const [loading, setLoading] = useState(false);
  const [documentTypes, setDocumentTypes] = useState<OptionType[]>([]);
  type T = typeof mode extends "add" ? AddTemplateType : EditTemplateType;
  const navigate = useNavigate();
  const addTemplateForm = useForm<T>({
    resolver: zodResolver(
      mode === "edit" ? EditTemplateSchema : AddTemplateSchema
    ),
    defaultValues:
      mode === "edit"
        ? {
            doctype_id: data.doctypeid + "",
            modelno: data.modelno + "",
            description: data.modeldesc,
            expression: data.hint_1,
            priority: data.priority! + "",
            file_extension: data.hint_2,
          }
        : {
            description: "",
            doctype_id: "",
            file_content: "",
            file_extension: "",
            expression: "",
            match_tag_1: "",
            match_tag_2: "",
            match_tag_3: "",
            match_tag_4: "",
            match_tag_5: "",
          },
  });

  async function fetchDocmentTypes() {
    const { data: docData, status: docStatus } = await getDocumentTypes();
    if (docStatus) {
      console.log("Document Types Data", docData);
      const docTypeOptions = docData.data.map((doc: any) => ({
        value: doc.documenttype,
        key: doc.docid,
      }));
      setDocumentTypes(docTypeOptions);
    }
  }
  useEffect(() => {
    void fetchDocmentTypes();
  }, []);
  const onSubmit = async (data: T) => {
    setLoading(true);
    try {
      if (mode === "edit") {
        delete data["doctype_id"];
        delete data["file_extension"];
      }
      const { status, data: response } =
        mode === "edit"
          ? await editTemplate(data.modelno + "", data)
          : await addTemplate({
              ...data,
              doctype_id: +data.doctype_id!,
              priority: data.priority ? +data.priority : 0,
            });
      if (status) {
        if (mode === "edit") {
            toast.success("Updated successfully!", { theme: "colored" });
          window.location.reload();
        } else {
          // add mode
          await new Promise((r) => setTimeout(r, 10000));

          navigate({
            to: templateEditHeaderRoute.fullPath,
            search: { id: (response as any).data },
          });
        }
      } else {
        toast.error("Error", {
          theme: "colored",
        });
      }
    } catch (error) {
      console.error("An error occured:", error);
    } finally {
      setLoading(false);
    }
  };
  return (
    <FormProvider {...addTemplateForm}>
      <form onSubmit={addTemplateForm.handleSubmit(onSubmit)}>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <TextBox
            className="parent-fw"
            name="description"
            label="Description"
            isRequired={true}
          />
          <TextBox name="expression" label="Expression" />
          <SelectBox
            name="file_extension"
            label="File Type"
            placeholder="Select File Type"
            options={[
              { key: "pdf", value: "pdf" },
              { key: "tif", value: "tif" },
            ]}
            isRequired={mode !== "edit"}
            disabled={mode === "edit"}
          />
          <SelectBox
            name="doctype_id"
            label="Document Type"
            placeholder="Select Document Type"
            options={documentTypes}
            isRequired={mode !== "edit"}
            disabled={mode === "edit"}
          />
          <CustomFileInput
            name="file"
            label="File"
            isRequired={mode !== "edit"}
            disabled={mode === "edit"}
          />
          <TextBox
            name="priority"
            label="Priority"
            type="number"
            min="1"
            max="10"
          />
        </div>
        <div className="mt-4 flex justify-center">
          <SubmitButton
            className="w-36"
            role="button"
            radius="full"
            size="medium"
            type="submit"
          >
            {loading ? (
              <div className="flex justify-center">
                <div className="spinner-border animate-spin border-4 border-orange-400 border-t-transparent w-5 h-5 rounded-full"></div>
              </div>
            ) : (
              "Submit"
            )}
          </SubmitButton>
        </div>
      </form>
    </FormProvider>
  );
};

export default AddTemplateForm;
